import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { ReactNode, useRef, useState } from 'react';
import { getScoreBarColorClass } from './score-bar-color';
import { CATEGORY_SUB_SCORE_LABELS } from './score-breakdown-labels';

type ScoreCategoryTooltipProps = {
  categoryKey: string;
  breakdown: Record<string, number> | undefined;
  children: ReactNode;
};

export default function ScoreCategoryTooltip({
  categoryKey,
  breakdown,
  children
}: ScoreCategoryTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({ fallbackAxisSideDirection: 'start' }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ]
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  const labelMap = CATEGORY_SUB_SCORE_LABELS[categoryKey] ?? {};
  const entries = breakdown
    ? Object.entries(breakdown)
        .filter(([key]) => key in labelMap)
        // Show sub-components first, then the total (shorter key) last
        .sort(([a], [b]) => b.length - a.length || a.localeCompare(b))
    : [];

  // Don't render tooltip trigger if there's no breakdown data
  if (entries.length === 0) {
    return children;
  }

  return (
    <>
      <div
        className='cursor-help'
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {children}
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            className='z-50 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg'
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <FloatingArrow
              fill='white'
              stroke='#e5e7eb'
              strokeWidth={1}
              ref={arrowRef}
              context={context}
            />
            <div className='flex flex-col gap-1.5'>
              {entries.map(([key, value]) => {
                const label = labelMap[key] ?? key;
                const clamped = Math.max(0, Math.min(100, value));
                const isTotal = key.length === 1;
                return (
                  <div key={key}>
                    {isTotal && (
                      <div className='mb-1 mt-1 border-t border-gray-100' />
                    )}
                    <div className='flex items-center gap-2'>
                      <div className='w-[110px] shrink-0 text-[10px] text-gray-500'>
                        {label}
                      </div>
                      <div className='relative h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100'>
                        <div
                          className={`h-full rounded-full ${getScoreBarColorClass(
                            clamped
                          )}`}
                          style={{ width: `${clamped}%` }}
                        />
                      </div>
                      <div className='w-6 shrink-0 text-right text-[10px] font-semibold text-gray-700'>
                        {Math.round(clamped)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
